/**
 * The retailer charge map, resolved.
 *
 * Replaces chargeSectionRegistry's name-only guessing. Matching is EXACT on the
 * normalised bill item within a bill section — no substring, no prefix, no
 * similarity. Job 71 ("mapping 1 section of invoice maps anything with a similar
 * name also") is that failure mode, so recall is deliberately traded for
 * precision. A miss returns null and the line is reported unmapped; nothing is
 * guessed.
 *
 * Pure and sync: rows are PASSED IN, the same way comparisonRows already takes
 * aliases and exclusions, so no async enters the tested pure layer.
 */
import type { ShadowBillSection } from '../types/billTypes';
import { canonicalName, nameKey, type NameAliases } from './nameAliases';

export type ChargeMapRow = {
  /** NULL = not distributor-scoped ('N/A'). 'MULTI' = any distributor. */
  distributor: string | null;
  /** NULL = any tariff. */
  tariff_code: string | null;
  /**
   * The canonical section, not the retailer's raw wording. ActualBillLine only
   * ever carries `section: ShadowBillSection` (the canonical enum) — the raw
   * bill-section wording is seeding provenance and is never preserved anywhere
   * at runtime — so this is the only section value the resolver can match on.
   */
  bill_section_canonical: ShadowBillSection;
  bill_item: string;
  expected_item: string | null;
  expected_section: ShadowBillSection;
  demand_unit: 'kW' | 'kVA' | null;
  group_key: string | null;
  map_state: 'validated' | 'passthrough' | 'unvalidated' | 'needs_review' | 'group_member';
};

const norm = (v: string | null | undefined) => (v ?? '').trim().toLowerCase();

/**
 * A bill item's key: case, surrounding space, and hyphen/underscore/space
 * differences folded, the same fold comparisonRows' pairKey uses. Retailers
 * print "Off-Peak Energy" and "Off Peak Energy" for one charge. Still exact
 * word-for-word: "Of Peak Energy" does not become "Off Peak Energy".
 */
export const itemKey = (v: string | null | undefined) =>
  norm(v).replace(/[-_]+/g, ' ').replace(/\s+/g, ' ');

/**
 * The distributors a row is written for. The sheet sometimes lists several in
 * one cell, separated by newlines (SmartestEnergy's network "Off Peak Energy"
 * names seven), commas or semicolons. Read as one string such a cell matched
 * no distributor at all.
 */
const distributorsOf = (r: ChargeMapRow): string[] =>
  norm(r.distributor).split(/[\n\r,;]+/).map((d) => d.trim()).filter(Boolean);

/**
 * A distributor's match key: its approved System Name (migration 138), folded
 * to letters and digits. The map says "Ergon" where the site says "Ergon
 * Energy"; both are Ergon Energy (Distribution). Without aliases this is the
 * plain spelling, folded the same way.
 */
const distKey = (aliases: NameAliases | undefined, d: string | null | undefined) =>
  nameKey(canonicalName(aliases, d));

const isFor = (r: ChargeMapRow, dist: string, aliases?: NameAliases) =>
  !!dist && distributorsOf(r).some((d) => distKey(aliases, d) === dist);
/** A row naming only this distributor ranks above a list that includes it. */
const singleFirst = (hits: ChargeMapRow[]) =>
  [...hits].sort((a, b) => distributorsOf(a).length - distributorsOf(b).length);

/** A row that has not been decided yet must not silently take effect. */
const isActive = (r: ChargeMapRow) => r.map_state !== 'needs_review';

/**
 * Among rows that tie on distributor/tariff specificity, prefer one whose
 * expected_item is set. The sheet can carry two rows sharing a key where one
 * has a blank target (e.g. a group-member placeholder); returning the blank
 * one first would silently discard a real mapping.
 */
const preferMapped = (hits: ChargeMapRow[]): ChargeMapRow =>
  hits.find((r) => r.expected_item !== null) ?? hits[0];

/**
 * Most specific wins, run once over a candidate set:
 *   1. distributor exact + tariff exact
 *   2. distributor exact + tariff null
 *   3. 'MULTI'          + tariff null
 *   4. tariff-only match (Shell Energy leaves Distributor blank, so the
 *      tariff alone separates NSP76 - Peak from A34C - Peak)
 *   5. distributor null (N/A)
 * Returns null if nothing in this candidate set matches any tier.
 */
function resolveWithinCandidates(
  candidates: ChargeMapRow[],
  dist: string,
  tariff: string,
  aliases?: NameAliases,
): ChargeMapRow | null {
  const tiers: Array<(r: ChargeMapRow) => boolean> = [
    (r) => isFor(r, dist, aliases) && !!tariff && norm(r.tariff_code) === tariff,
    (r) => isFor(r, dist, aliases) && !r.tariff_code,
    (r) => norm(r.distributor) === 'multi' && !r.tariff_code,
    // A tariff-only discriminator: Shell Energy leaves Distributor blank, so the
    // tariff alone separates NSP76 - Peak from A34C - Peak. Restricted to
    // distributor-null rows on purpose — without that, a row carrying a real
    // distributor could be matched for a DIFFERENT distributor that happens to
    // share a tariff code, pricing the charge against the wrong network tariff.
    //
    // Ahead of the general N/A tier: a row written for THIS tariff is the more
    // specific one. After it, Shell's general "Peak" (Anytime Usage, observed on
    // 8300) beat its LBAD "Peak" (Peak Usage) on every LBAD site.
    (r) => !!tariff && r.distributor === null && norm(r.tariff_code) === tariff,
    // Tariff must ALSO be null here, not just distributor: an N/A row with a
    // non-null tariff_code belongs to the tier above, not to every tariff.
    (r) => r.distributor === null && !r.tariff_code,
  ];

  for (const tier of tiers) {
    const hits = candidates.filter(tier);
    if (hits.length > 0) return preferMapped(singleFirst(hits));
  }

  return null;
}

/**
 * The bill item with the SITE's own tariff code taken off the front.
 *
 * Shell Energy prints every network line as "<tariff> - <item>" ("LBAD - Peak",
 * "EA305 - Capacity", 1,630 lines on 400 prod bills) while its map rows hold the
 * bare wording, so none of them matched. Only the site's exact code is removed:
 * "MBTOUD - Peak" on an LBAD site is left alone.
 *
 * The code may be followed by a hyphen or just a space: Shell's Powercor and
 * United invoices print "LLV2 Peak Energy", "NDMO21 Off Peak Energy" (178 prod
 * lines from July 2026, ~$239k unmatched). A code glued to the next word
 * ("LLV2Peak") is not split.
 */
function withoutSiteTariff(billItem: string, tariff: string | null): string {
  const code = (tariff ?? '').trim();
  if (!code) return billItem;
  const escaped = code.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return billItem.replace(new RegExp(`^\\s*\\(?${escaped}\\)?(?:\\s*-\\s*|\\s+)`, 'i'), '');
}

export function resolveChargeMap(
  rows: ChargeMapRow[],
  q: {
    distributor: string | null; tariff: string | null; billSection: string; billItem: string;
    /**
     * Never fall back to a row written for another section. Set when the bill
     * prints this same name in more than one section: those are separate
     * charges, and borrowing the other section's row adds one onto the other.
     */
    strictSection?: boolean;
    /**
     * Whether a target is one of THIS bill's expected charges. When several
     * rows share the wording ("Peak" is Anytime Usage on Shell's 8300 and Peak
     * Usage on LBAD), the rows whose target is on the bill are tried first;
     * if none is, every row stays in play and the usual order decides.
     */
    isExpected?: (expectedItem: string) => boolean;
    /** Approved distributor names and their other spellings; see distKey. */
    distributorAliases?: NameAliases;
  },
): ChargeMapRow | null {
  const item = itemKey(withoutSiteTariff(q.billItem, q.tariff));
  const section = norm(q.billSection);
  const aliases = q.distributorAliases;
  const dist = distKey(aliases, q.distributor);
  const tariff = norm(q.tariff);

  // A needs_review row is an undecided mapping, so it is dropped before either
  // pass below gets a chance to match it.
  const active = rows.filter((r) => isActive(r) && itemKey(r.bill_item) === item);
  if (active.length === 0) return null;

  const onBill = (cands: ChargeMapRow[]): ChargeMapRow[] => {
    if (!q.isExpected || cands.length < 2) return cands;
    const hits = cands.filter((r) => r.expected_item !== null && q.isExpected!(r.expected_item));
    return hits.length > 0 ? hits : cands;
  };

  // Section-first, then section-agnostic.
  //
  // ActualBillLine.section is currently POST-reclassification — heuristics move
  // a line to its canonical home, so it approximates the *expected* section.
  // A later task removes those heuristics, after which the same field will
  // instead approximate the invoice's own raw heading. The two regimes differ
  // for rows like AGL's "Other Charges -> Metering". Trying the query's section
  // first is correct either way it settles; falling back to every candidate
  // (ignoring section) is what catches the cases where they diverge, rather
  // than silently missing under one of the two regimes.
  //
  // The fallback is only for an item this section does not map at all. When the
  // section DOES map it — just not for this distributor — a miss is a gap in the
  // map, and borrowing a row written for another section is how Next Business
  // Energy's network "Peak" (Powercor, no network row) took the retail "Peak"
  // row and was added onto the retail charge. Report it unmapped instead.
  const sectionScoped = onBill(active.filter((r) => norm(r.bill_section_canonical) === section));
  if (sectionScoped.length > 0) {
    const hit = resolveWithinCandidates(sectionScoped, dist, tariff, aliases);
    if (hit || section !== 'retail') return hit;
    // A RETAIL row written for one distributor: the retailer's own retail
    // wording means the same whatever the network (SmartestEnergy's "Retail
    // Service Fee" row names only Ergon Energy, yet the fee is on its Powercor
    // invoices too). Applied only when every such row agrees on the target, so
    // there is nothing to choose between. Network rows never do this: a network
    // charge is priced per distributor, and borrowing another distributor's row
    // is the wrong-tariff failure described above.
    const targets = new Set(sectionScoped.map((r) => `${itemKey(r.expected_item)}|${r.expected_section}`));
    return targets.size === 1 ? preferMapped(sectionScoped) : null;
  }
  if (q.strictSection) return null;
  return resolveWithinCandidates(onBill(active), dist, tariff, aliases);
}
